import { Badge, Box, Container, DataList, Flex, Grid, Heading, Inset, Section, Tabs, Text } from '@radix-ui/themes'
import { type MetaArgs, type MetaFunction } from '@remix-run/node'
import {
	type ShouldRevalidateFunctionArgs,
	Outlet,
	useLoaderData,
	useNavigation,
	useOutletContext,
	useSearchParams,
} from '@remix-run/react'
import isEqual from 'lodash.isequal'
import uniqWith from 'lodash.uniqwith'
import * as React from 'react'
import { type Params } from 'react-router-dom'
import YouTube from 'react-youtube'

import { z } from 'zod'
import { ProductCollapseContext } from '~/components/common/product-collapse'
import { ProductGridItem } from '~/components/common/product-grid-item'
import { WishlistAction } from '~/components/common/wishlist-action'
import { IMAGE_FILTERS } from '~/components/ui/image'
import { type GetProduct } from '~/models/ecommerce-providers/sylius/shop/fetch/product/get-product'
import { type GetProductAssociation } from '~/models/ecommerce-providers/sylius/shop/fetch/product-association/get-product-association'
import { type GetProductImage } from '~/models/ecommerce-providers/sylius/shop/fetch/product-image/get-product-image'
import { type GetProductVariant } from '~/models/ecommerce-providers/sylius/shop/fetch/product-variant/get-product-variant'
import { type Locale } from '~/models/locale'
import { NEWSLETTER_FORM } from '~/routes/($lang).newsletter/newsletter.component'
import { ProductInfoGrid } from '~/routes/($lang).products.$slug/product-info-grid'
import { ProductSidebar } from '~/routes/($lang).products.$slug/product-sidebar'
import { ProductSlider } from '~/routes/($lang).products.$slug/product-slider'
import { type loader } from '~/routes/($lang).products.$slug/slug.server'
import {
	type Context,
	type FormEnum,
	ROOT_INPUTS,
} from '~/routes/_layout/root.component'
import { type loader as RouteLoader } from '~/routes/_layout/root.server'
import { defaultMetaI18next } from '~/utils/client/default-meta-i18next.ts'
import { generateItemFormFetcherKey } from '~/utils/client/generate-item-form-fetcher-key.ts'
import { generateItemFormId } from '~/utils/client/generate-item-form-id.ts'
import { schemaDefault } from '~/utils/form-defaults.ts'
import { imagePathFilter } from '~/utils/image-path-filter.ts'
import { useTranslation } from 'react-i18next'
import parse from 'html-react-parser'
import { Price } from '~/components/ui/price.tsx'
import { appConfig } from '~/app-config.ts'
import { ProductOptions } from '~/routes/($lang).products.$slug/product-options.tsx'
import { ProductAddToCart } from '~/components/common/product-add-to-cart.tsx'
import { ProductsSlider } from '~/components/common/products-slider.tsx'

/* -------------------------------------------------------------------------------------------------
 * I18next
 * -----------------------------------------------------------------------------------------------*/

enum I18NEXT {
	routeProductsSlug = 'route-products-slug',
	uiCollapse = 'ui-collapse',
	uiProductSlider = 'ui-product-slider',
	uiWishlistAction = 'ui-wishlist-action',
}

/* -------------------------------------------------------------------------------------------------
 * Route loader
 * -----------------------------------------------------------------------------------------------*/

enum ROUTE_LOADER {
	root = 'root',
	current = 'routes/($lang).products.$slug',
}

/* -------------------------------------------------------------------------------------------------
 * Search params
 * -----------------------------------------------------------------------------------------------*/

enum SEARCH_PARAMS {
	reviewCreated = 'review-created',
}

/* -------------------------------------------------------------------------------------------------
 * Inputs
 * -----------------------------------------------------------------------------------------------*/

enum INPUTS {
	variantId = 'variantId',
	quantity = 'quantity',
	variantImage = 'variantImage',
	variantPrice = 'variantPrice',
	name = 'name',
	slug = 'slug',
	variantOptionsValuesLength = 'variantOptionsValuesLength',
	variantOptionsValues_name = 'variantOptionsValues[][name]',
	variantOptionsValues_code = 'variantOptionsValues[][code]',
	variantOptionsValues_value = 'variantOptionsValues[][value]',
}

/* -------------------------------------------------------------------------------------------------
 * Add to cart Form
 * -----------------------------------------------------------------------------------------------*/

const FORM_ADD_TO_CART: FormEnum = {
	generateId: ({ id }) => generateItemFormId('add-to-cart-form', id),
	generateAction: (params: Params) => `/products/${params.slug}`,
	generateFetcherKey: ({ id }) => generateItemFormFetcherKey('add-to-cart', id),
	subaction: 'add-to-cart',
	generateRedirect: (params: Params) => `/products/${params.slug}`,
}

const addToCartFormSchema = z.object({
	...schemaDefault,
  ...{
    [INPUTS.variantId]: z
      .string(),
    [INPUTS.quantity]: z
      .number(),
    [INPUTS.slug]: z
      .string(),
    [INPUTS.variantImage]: z
      .string(),
    [INPUTS.variantPrice]: z
      .number(),
    [INPUTS.name]: z
      .string(),
    [INPUTS.variantOptionsValuesLength]: z
      .number()
      .optional(),
  }
})

/* -------------------------------------------------------------------------------------------------
 * Select option Form
 * -----------------------------------------------------------------------------------------------*/

const FORM_SELECT_OPTION: FormEnum<{ id: string; value: string }> = {
	generateId: ({ id }) => generateItemFormId('select-option-form', id),
	generateAction: (params: Params) => `/products/${params.slug}`,
	generateFetcherKey: ({ id }) =>
		generateItemFormFetcherKey('select-option', id),
	subaction: 'select-option',
	generateRedirect: (params: Params) => `/products/${params.slug}`,
}

/* -------------------------------------------------------------------------------------------------
 * Handle
 * -----------------------------------------------------------------------------------------------*/

const handle = { i18n: [...Object.values(I18NEXT)] }

/* -------------------------------------------------------------------------------------------------
 * Meta
 * -----------------------------------------------------------------------------------------------*/

const meta: MetaFunction<
	typeof loader,
	{
		[ROUTE_LOADER.root]: typeof RouteLoader
	}
> = ({ data, matches }: MetaArgs) => {
	const parentMeta = matches.reduce((a, b) =>
		a.meta.length > b.meta.length ? a : b,
	).meta
	const title = parentMeta.find((meta) => 'title' in meta)
	const description = parentMeta.find(
		(meta) => 'name' in meta && meta.name === 'description',
	)

	const rootData = matches.find((match) => match.id === ROUTE_LOADER.root)!
		.data as { locale: Locale }

	// @ts-ignore
	return uniqWith(
		title?.title.includes('Error')
			? [...parentMeta]
			: data
				? [
						...parentMeta.filter(
							(meta) =>
								!Object.entries(meta).find(
									([key, value]) =>
										key === 'title' ||
										(key === 'name' && value === 'description') ||
										(key === 'name' && value === 'keywords') ||
										(key === 'property' && value === 'og:title') ||
										(key === 'property' && value === 'og:description') ||
										(key === 'property' && value === 'og:image') ||
										(key === 'property' && value === 'og:image:width') ||
										(key === 'property' && value === 'og:image:height') ||
										(key === 'property' && value === 'og:image:alt'),
								),
						),
						// @ts-ignore
						{ title: `${data.product.name} | ${title?.title}` },
						{
							name: 'description',
							// @ts-ignore
							content: `${data.product.metaDescription} | ${description?.content}`,
						},
						{
							name: 'keywords',
							// @ts-ignore
							content: `${data.product.metaKeywords}`,
						},
						// Open Graph
						{
							property: 'og:title',
							// @ts-ignore
							content: data.product.name,
						},
						{
							property: 'og:description',
							// @ts-ignore
							content: data.product.description.replace(/(<([^>]+)>)/gi, ''),
						},
						{
							property: 'og:image',
							// @ts-ignore
							content: data.product.images?.[0].path ?? '',
						},
						{
							property: 'og:image:width',
							// @ts-ignore
							content: data.product.images?.[0].width ?? 800,
						},
						{
							property: 'og:image:height',
							// @ts-ignore
							content: data.product.images?.[0].height ?? 800,
						},
						{
							property: 'og:image:alt',
							// @ts-ignore
							content: data.product.name,
						},
						// Structured data -> ld+json
						{
							// @ts-ignore
							'script:ld+json': data.product,
						},
						/*{
      'script:ld+json': data.associations,
    {
      'script:ld+json': data.attributes,
    },
    },*/
						// Preload images
						// @ts-ignore
						(
							data.product.images?.filter(
								({ path }: GetProductImage) => !!path,
							) || []
						)
							.slice(0, 5)
							.map(({ path }: GetProductImage) => ({
								tagname: 'link',
								rel: 'preload',
								href: imagePathFilter({
									path: path!,
									filter: IMAGE_FILTERS.sliderFilter,
								}),
								as: 'image',
							}))
							.slice(0, 1),
						// I18next
						...defaultMetaI18next<typeof I18NEXT>({
							I18NEXT,
							locale: rootData.locale,
						}),
					]
				: [
						...parentMeta.filter(
							(meta) =>
								!Object.entries(meta).find(
									([key, value]) =>
										key === 'title' ||
										(key === 'name' && value === 'description'),
								),
						),
						// @ts-ignore
						{ title: `Error | ${title?.title}` },
						{
							name: 'description',
							// @ts-ignore
							content: `Error | ${description?.content}`,
						},
					],
		(a, b) => isEqual(a, b),
	)
}

/* -------------------------------------------------------------------------------------------------
 * Should revalidate
 * -----------------------------------------------------------------------------------------------*/

const shouldRevalidate = ({
	formData,
	defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) => {
	if (
		formData?.get(ROOT_INPUTS.subaction) === FORM_ADD_TO_CART.subaction ||
		formData?.get(ROOT_INPUTS.subaction) === NEWSLETTER_FORM.subaction
	) {
		return false
	}

	return defaultShouldRevalidate
}

/* -------------------------------------------------------------------------------------------------
 * Component
 * -----------------------------------------------------------------------------------------------*/

const Component: React.FC = () => {
	const {
		product,
		selectedVariant,
		wishlist,
		gridInfo,
		associations,
		mainImage,
	} = useLoaderData<typeof loader>()
	const context = useOutletContext<Context>()
	const [collapseOpen, setCollapseOpen] = React.useState(false)
	const [collapseHighlight, setCollapseHighlight] = React.useState('')
	const { locale } = context
	const { location } = useNavigation()
	const [currentSearchParams] = useSearchParams()
	const searchParams = location?.search
		? new URLSearchParams(location.search)
		: currentSearchParams
	const inWishlist = wishlist?.includes(product.id) ?? false
	const displayWishlist = typeof wishlist !== 'undefined'
	const [isMobile, setIsMobile] = React.useState(true)

	React.useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 1280)
		}

		handleResize()
		window.addEventListener('resize', handleResize)

		return () => window.removeEventListener('resize', handleResize)
	}, [])
  const { t } = useTranslation(I18NEXT.routeProductsSlug)

	return (
		<ProductCollapseContext.Provider
			key={product.id}
			value={React.useMemo(
				() => ({
					collapseHighlight,
					setCollapseHighlight,
					collapseOpen,
					setCollapseOpen,
				}),
				[
					collapseHighlight,
					setCollapseHighlight,
					collapseOpen,
					setCollapseOpen,
				],
			)}
		>
			<main>
				<Container px="5" mt="9">
					<Grid columns={{ initial: '1', lg: '12' }} gap="7" position="relative">
						<Box
              position="relative"
              gridColumn={{ lg: 'span 5' }}
            >
              {!!product.badges?.length && (
                <Inset clip="padding-box" side="top" pb="current">
                  <Flex align="center" gapX="1" position="absolute" top="-1px">
                    {product.badges.map((badge) => (
                      <Badge key={badge} color="red">{badge}</Badge>
                    ))}
                  </Flex>
                </Inset>
              )}
							<ProductSlider
								product={product as GetProduct}
								mainImage={mainImage}
							/>
						</Box>

						<Flex
							justify="center"
							mt={{ lg: '44' }}
							gridColumn={{ lg: 'span 4' }}
						>
							<ProductSidebar
								product={product as GetProduct}
								searchParams={searchParams}
								variant={selectedVariant as GetProductVariant}
								locale={locale}
								key={product.id}
							/>
						</Flex>

						<Flex
              direction="column"
							justify="center"
							mt={{ lg: '9' }}
							gridColumn={{ lg: 'span 3' }}
						>
              <Flex direction="column" align="center" gapY="2">
                <Flex justify="center" align="center" position="relative">
                  <Price locale={locale} size="7" weight="medium">
                    {selectedVariant.price!}
                  </Price>
                </Flex>
                <Badge color="gray">
                  {t('common.omnibusPriceAlert-$val-$count', {
                    val:
                      (selectedVariant.lowestPriceBeforeDiscount || selectedVariant.price!) / 100,
                    count: appConfig.lowest_price_before_discount_days,
                    formatParams: {
                      val: {
                        currency: appConfig.currency,
                        locale: locale,
                      },
                    },
                  })}
                </Badge>
              </Flex>
              <ProductOptions
                product={product}
                searchParams={searchParams}
                selectedVariant={selectedVariant}
              />
              <ProductAddToCart
                label={t('button.addToCart')}
                product={product}
                variant={selectedVariant}
                t={t}
              />
						</Flex>
					</Grid>

          <Section>
            <Tabs.Root defaultValue="description">
              <Tabs.List>
                {product.description && (
                  <Tabs.Trigger value="description">{t('common.tabs.description')}</Tabs.Trigger>
                )}
                {product.downloads && (
                  <Tabs.Trigger value="downloads">{t('common.tabs.downloads')}</Tabs.Trigger>
                )}
                {product.videos && (
                  <Tabs.Trigger value="videos">{t('common.tabs.videos')}</Tabs.Trigger>
                )}
              </Tabs.List>

              <Box pt="3">
                {product.description && (
                  <Tabs.Content value="description">
                    <Flex direction="column" gapY="6" mt="5">
                      <DataList.Root>
                        <DataList.Item align="center">
                          <DataList.Label minWidth="88px">{t('common.manufacturer')}</DataList.Label>
                          <DataList.Value>
                            {product.manufacturer}
                          </DataList.Value>
                        </DataList.Item>
                        {Object.entries(product.properties)?.map(([key, value]) => (
                          <DataList.Item key={key} align="center">
                            <DataList.Label minWidth="88px">{key}</DataList.Label>
                            <DataList.Value>
                              {value}
                            </DataList.Value>
                          </DataList.Item>
                        ))}
                      </DataList.Root>
                      <Box className="app-ProductDescription max-w-3xl">
                        {parse(product.description)}
                      </Box>
                    </Flex>
                  </Tabs.Content>
                )}
                {product.downloads && (
                  <Tabs.Content value="downloads">
                    <Text size="2">Downloads</Text>
                  </Tabs.Content>
                )}
                {product.videos && (
                  <Tabs.Content value="videos">
                    <Heading my="5">Our videos from YouTube</Heading>
                    <Flex direction="column" gapY="5">
                      {product.videos.map((video) => (
                        <YouTube
                          key={video}
                          videoId={video}
                          className="app-YoutubeContainer"
                        />
                      ))}
                    </Flex>
                  </Tabs.Content>
                )}
              </Box>
            </Tabs.Root>
          </Section>
        </Container>

        {!!associations?.related.length && (
          <Section>
            <ProductsSlider
              products={associations.related}
              title={t('common.related.title')}
            />
          </Section>
        )}

        {!!associations?.similar.length && (
          <Section>
            <ProductsSlider
              products={associations.similar}
              title={t('common.similar.title')}
            />
          </Section>
        )}
			</main>
		</ProductCollapseContext.Provider>
	)
}
Component.displayName = 'Component'

/* -----------------------------------------------------------------------------------------------*/

export {
	Component,
	shouldRevalidate,
	handle,
	meta,
	I18NEXT as PRODUCTS_SLUG_I18NEXT,
	ROUTE_LOADER as PRODUCTS_SLUG_ROUTE_LOADER,
	SEARCH_PARAMS as PRODUCTS_SLUG_SEARCH_PARAMS,
	INPUTS as PRODUCTS_SLUG_INPUTS,
	FORM_ADD_TO_CART as PRODUCTS_SLUG_FORM_ADD_TO_CART,
	FORM_SELECT_OPTION as PRODUCTS_SLUG_FORM_SELECT_OPTION,
	addToCartFormSchema as productsSlugAddToCartFormSchema,
}
